import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Plus, ExternalLink, Pencil, Power, X, IdCard, Eye, UserPlus, Inbox, Archive, RotateCcw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import api, { formatApiError } from "@/lib/api";
import { StatusBadge, Spinner } from "@/components/dashboard/DashBits";

function NewProfileModal({ companies, onClose, onCreated }) {
  const { t } = useI18n();
  const [companyId, setCompanyId] = useState(companies[0]?.id || "");
  const [loading, setLoading] = useState(false);
  const create = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data } = await api.post("/admin/profiles", { company_id: companyId, data: {} });
      toast.success(t("dash.profileCreated", { code: data.code }));
      onCreated(data.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="new-profile-modal">
      <div className="glass-strong rounded-2xl w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-white">{t("dash.newProfileTitle")}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <label className="text-xs text-zinc-500">{t("dash.company")}</label>
        <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="new-profile-company"
          className="w-full mt-1 bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm focus-ring">
          {companies.map((c) => <option key={c.id} value={c.id} className="bg-[#14141f]">{c.name}</option>)}
        </select>
        <button onClick={create} disabled={loading} data-testid="new-profile-create"
          className="w-full mt-5 bg-brand-gradient text-white font-semibold py-3 rounded-xl disabled:opacity-60">
          {loading ? t("dash.creating") : t("dash.createAndEdit")}
        </button>
      </div>
    </div>
  );
}

export default function Profiles() {
  const { user } = useAuth();
  const { t } = useI18n();
  const isAdmin = user.role === "superadmin";
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState("");
  const [showNew, setShowNew] = useState(false);

  const load = () => {
    const base = isAdmin ? "/admin/profiles" : "/company/profiles";
    api.get(`${base}?search=${encodeURIComponent(search)}&active=${active}`).then(({ data }) => setProfiles(data));
  };
  useEffect(() => { if (isAdmin) api.get("/admin/companies").then(({ data }) => setCompanies(data)); }, [isAdmin]);
  useEffect(() => { const tm = setTimeout(load, 250); return () => clearTimeout(tm); }, [search, active]);

  const toggle = async (p) => {
    const base = isAdmin ? "/admin/profiles" : "/company/profiles";
    try {
      await api.patch(`${base}/${p.id}/toggle`);
      toast.success(t("dash.statusUpdated"));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const archive = async (p) => {
    if (!window.confirm(t("dash.confirmArchiveProfile"))) return;
    try {
      await api.delete(`/admin/profiles/${p.id}`);
      toast.success(t("dash.profileArchived"));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const restore = async (p) => {
    try {
      await api.post(`/admin/profiles/${p.id}/restore`);
      toast.success(t("dash.profileRestored"));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl text-white">{isAdmin ? t("dash.profilesTitle") : t("dash.myProfilesTitle")}</h1>
          <p className="mt-1 text-zinc-400">{t("dash.profilesSub")}</p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowNew(true)} data-testid="profile-new"
            className="inline-flex items-center gap-2 bg-brand-gradient text-white font-semibold px-4 py-2.5 rounded-full">
            <Plus className="w-4 h-4" /> {t("dash.newProfile")}
          </button>
        )}
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input placeholder={t("dash.searchProfile")} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="profile-search"
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-white text-sm focus-ring" />
        </div>
        <select value={active} onChange={(e) => setActive(e.target.value)} data-testid="profile-filter"
          className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus-ring">
          <option value="" className="bg-[#14141f]">{t("dash.all")}</option>
          <option value="true" className="bg-[#14141f]">{t("dash.activesFilter")}</option>
          <option value="false" className="bg-[#14141f]">{t("dash.inactivesFilter")}</option>
          {isAdmin && <option value="archived" className="bg-[#14141f]">{t("dash.archivedFilter")}</option>}
        </select>
      </div>

      {profiles === null ? <Spinner /> : (
        <div className="mt-6 space-y-3" data-testid="profile-list">
          {profiles.length === 0 && <p className="text-zinc-500 py-10 text-center">{t("dash.noProfiles")}</p>}
          {profiles.map((p) => (
            <div key={p.id} className="rounded-2xl border border-white/10 bg-[#0d0d14] p-4 flex items-center gap-4" data-testid={`profile-row-${p.slug}`}>
              <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${p.data.primary_color || "#06b6d4"}22`, color: p.data.primary_color || "#06b6d4" }}>
                <IdCard className="w-5 h-5" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <span className="font-mono text-xs text-cyan-400">{p.code}</span>
                  <p className="font-medium text-white truncate">{`${p.data.first_name || ""} ${p.data.last_name || ""}`.trim() || t("dash.noName")}</p>
                  <StatusBadge status={p.active ? "active" : "inactive"} />
                </div>
                <p className="text-sm text-zinc-500 truncate">{p.data.job_title || "—"}{isAdmin && p.company_name ? ` · ${p.company_name}` : ""}</p>
              </div>
              {p.stats && (
                <div className="hidden sm:flex items-center gap-4 text-sm text-zinc-300 shrink-0" data-testid={`profile-stats-${p.slug}`}>
                  <span className="flex items-center gap-1.5" title={t("dash.col_opens")}><Eye className="w-4 h-4 text-blue-400" />{p.stats.views}</span>
                  <span className="flex items-center gap-1.5" title={t("dash.col_contacts")}><UserPlus className="w-4 h-4 text-emerald-400" />{p.stats.add_contact}</span>
                  <span className="flex items-center gap-1.5" title={t("dash.col_requests")}><Inbox className="w-4 h-4 text-amber-400" />{p.stats.contact_request}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <a href={`/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg glass text-zinc-300 hover:text-white" title={t("dash.viewCard")}><ExternalLink className="w-4 h-4" /></a>
                <button onClick={() => navigate(`/dashboard/profils/${p.id}`)} data-testid={`profile-edit-${p.slug}`} className="p-2 rounded-lg glass text-zinc-300 hover:text-white"><Pencil className="w-4 h-4" /></button>
                {p.archived ? (
                  isAdmin && <button onClick={() => restore(p)} data-testid={`profile-restore-${p.slug}`} className="p-2 rounded-lg glass text-emerald-400 hover:bg-emerald-500/10"><RotateCcw className="w-4 h-4" /></button>
                ) : (
                  <>
                    <button onClick={() => toggle(p)} data-testid={`profile-toggle-${p.slug}`} className={`p-2 rounded-lg glass ${p.active ? "text-emerald-400" : "text-zinc-500"}`}><Power className="w-4 h-4" /></button>
                    {isAdmin && <button onClick={() => archive(p)} data-testid={`profile-archive-${p.slug}`} className="p-2 rounded-lg glass text-amber-400 hover:bg-amber-500/10"><Archive className="w-4 h-4" /></button>}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && <NewProfileModal companies={companies} onClose={() => setShowNew(false)} onCreated={(id) => navigate(`/dashboard/profils/${id}`)} />}
    </div>
  );
}
